import { useEffect, useState } from "react";
import API from "../lib/api";
import ukMap from "../lib/ukMapPaths.json";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { GapRecruitmentList } from "./GapRecruitmentList";

// Map polygon → picker area name for click-to-filter (multi-county Scottish regions excluded)
const CLICK_AREA = {
  Durham: "County Durham", Antrim: "County Antrim", Armagh: "County Armagh",
  Down: "County Down", Fermanagh: "County Fermanagh", Londonderry: "County Londonderry",
  Tyrone: "County Tyrone", "Orkney Islands": "Orkney", "Shetland Islands": "Shetland",
  "Eilean Siar": "Western Isles",
};
const NO_CLICK = new Set(["Central", "Grampian", "Lothian", "Strathclyde", "Tayside"]);
const clickArea = (name) => (NO_CLICK.has(name) ? null : CLICK_AREA[name] || name);

const fillFor = (count, max) => {
  if (!count) return "#fca5a5";
  const t = Math.min(1, count / Math.max(1, max));
  if (t <= 0.2) return "#fde68a";
  if (t <= 0.4) return "#d9f99d";
  if (t <= 0.6) return "#a7f3d0";
  if (t <= 0.8) return "#34d399";
  return "#047857";
};

export const SupplierCoverageMap = ({ onSelectArea }) => {
  const [data, setData] = useState(null);
  const [hover, setHover] = useState(null);
  const [service, setService] = useState("");
  const [serviceOptions, setServiceOptions] = useState([]);
  const [gapCounty, setGapCounty] = useState("");

  useEffect(() => {
    API.get("/suppliers/service-options").then(({ data }) => setServiceOptions(data.services || [])).catch(() => {});
  }, []);

  useEffect(() => {
    API.get("/suppliers/coverage-map", { params: service ? { service } : {} })
      .then(({ data }) => setData(data)).catch(() => {});
  }, [service]);

  const counts = data?.counts || {};
  const max = data?.max_count || 0;
  const ranked = Object.entries(counts).filter(([, v]) => v.count > 0).sort((a, b) => b[1].count - a[1].count);
  const gaps = Object.entries(counts).filter(([, v]) => v.count === 0).map(([k]) => k);
  const allCounties = ukMap.counties.map((c) => c.name).sort((a, b) => a.localeCompare(b));
  const countyServices = new Set((data?.services_by_polygon?.[gapCounty] || []).map((s) => s.toLowerCase()));
  const missingServices = gapCounty ? serviceOptions.filter((s) => !countyServices.has(s.toLowerCase())) : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="coverage-map-section">
      <div className="lg:col-span-3 relative border border-border rounded-md bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
          <div>
            <p className="text-sm font-semibold mb-1">UK Supplier Coverage{service ? ` — ${service}` : ""}</p>
            <p className="text-xs text-muted-foreground">{service ? `Shaded by suppliers offering ${service} in each county — red = gap.` : "Shaded by suppliers covering each county — red = no coverage."} Click a county to filter the directory.</p>
          </div>
          <Select value={service || "all_services"} onValueChange={(v) => setService(v === "all_services" ? "" : v)}>
            <SelectTrigger className="w-52 text-xs h-8" data-testid="coverage-service-filter"><SelectValue placeholder="All services" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all_services">All services</SelectItem>
              {serviceOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <svg viewBox={ukMap.viewBox} className="w-full max-h-[70vh]" data-testid="coverage-map-svg">
          {ukMap.counties.map((c) => {
            const entry = counts[c.name] || { count: 0, suppliers: [] };
            return (
              <path
                key={c.name}
                d={c.d}
                fill={fillFor(entry.count, max)}
                stroke="#ffffff"
                strokeWidth="1.5"
                className={clickArea(c.name) ? "cursor-pointer transition-opacity hover:opacity-75" : "transition-opacity hover:opacity-75"}
                data-testid={`coverage-county-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.ownerSVGElement.parentNode.getBoundingClientRect();
                  setHover({ name: c.name, ...entry, x: e.clientX - rect.left, y: e.clientY - rect.top });
                }}
                onMouseLeave={() => setHover(null)}
                onClick={() => {
                  const area = clickArea(c.name);
                  if (area && onSelectArea) onSelectArea(area);
                }}
              />
            );
          })}
        </svg>
        {hover && (
          <div
            className="absolute z-20 pointer-events-none bg-popover border border-border rounded-md shadow-lg px-3 py-2 max-w-56"
            style={{ left: Math.min(hover.x + 14, 520), top: hover.y + 10 }}
            data-testid="coverage-tooltip"
          >
            <p className="text-xs font-semibold">{hover.name}</p>
            <p className={`text-xs ${hover.count ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"}`}>
              {hover.count ? `${hover.count} supplier${hover.count === 1 ? "" : "s"}${service ? ` — ${service}` : ""}` : (service ? `No ${service} coverage` : "No coverage")}
            </p>
            {hover.suppliers?.length > 0 && (
              <p className="text-[10px] text-muted-foreground mt-1">
                {hover.suppliers.slice(0, 6).join(", ")}{hover.count > 6 ? ` +${hover.count - 6} more` : ""}
              </p>
            )}
          </div>
        )}
        <div className="flex items-center gap-3 mt-3 flex-wrap text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: "#fca5a5" }} /> Gap</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: "#fde68a" }} /> Low</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: "#a7f3d0" }} /> Medium</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm inline-block" style={{ background: "#047857" }} /> High</span>
        </div>
      </div>

      <div className="lg:col-span-2 space-y-4">
        <GapRecruitmentList />
        <div className="grid grid-cols-3 gap-3">
          <div className="border border-border rounded-md p-3 bg-card" data-testid="coverage-stat-suppliers">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Suppliers</p>
            <p className="text-2xl font-bold font-[Chivo]">{data?.total_suppliers ?? "—"}</p>
          </div>
          <div className="border border-border rounded-md p-3 bg-card" data-testid="coverage-stat-covered">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Covered</p>
            <p className="text-2xl font-bold font-[Chivo] text-emerald-600 dark:text-emerald-400">{data?.covered ?? "—"}<span className="text-xs text-muted-foreground font-normal">/73</span></p>
          </div>
          <div className="border border-border rounded-md p-3 bg-card" data-testid="coverage-stat-gaps">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Gaps</p>
            <p className="text-2xl font-bold font-[Chivo] text-red-500">{data?.gaps ?? "—"}</p>
          </div>
        </div>

        <div className="border border-border rounded-md bg-card p-3" data-testid="service-gap-finder">
          <p className="text-xs font-semibold mb-2">Service gap finder</p>
          <p className="text-[10px] text-muted-foreground mb-2">Pick a county to see which services have no supplier there.</p>
          <Select value={gapCounty || "none"} onValueChange={(v) => setGapCounty(v === "none" ? "" : v)}>
            <SelectTrigger className="w-full text-xs h-8" data-testid="gap-county-select"><SelectValue placeholder="Choose a county..." /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="none">Choose a county...</SelectItem>
              {allCounties.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          {gapCounty && (
            <div className="mt-2" data-testid="gap-county-results">
              <p className="text-xs mb-1.5">
                <span className="font-semibold text-red-500">{missingServices.length}</span>
                <span className="text-muted-foreground"> of {serviceOptions.length} services missing in </span>
                <span className="font-semibold">{gapCounty}</span>
              </p>
              <div className="flex flex-wrap gap-1 max-h-44 overflow-y-auto">
                {missingServices.map((s) => (
                  <Badge key={s} variant="outline" className="text-[9px] text-red-600 dark:text-red-400 border-red-200 dark:border-red-900">{s}</Badge>
                ))}
                {missingServices.length === 0 && <p className="text-xs text-emerald-600 dark:text-emerald-400">All services covered in {gapCounty} 🎉</p>}
              </div>
            </div>
          )}
        </div>

        <div className="border border-border rounded-md bg-card p-3" data-testid="coverage-top-list">
          <p className="text-xs font-semibold mb-2">Best covered</p>
          <div className="space-y-1.5">
            {ranked.slice(0, 10).map(([name, v]) => (
              <div key={name} className="flex items-center gap-2">
                <span className="text-xs w-40 truncate">{name}</span>
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(v.count / Math.max(1, max)) * 100}%`, background: fillFor(v.count, max) }} />
                </div>
                <span className="text-xs font-mono font-medium w-6 text-right">{v.count}</span>
              </div>
            ))}
            {ranked.length === 0 && <p className="text-xs text-muted-foreground">No coverage data yet — set Areas Covered on your suppliers.</p>}
          </div>
        </div>

        <div className="border border-border rounded-md bg-card p-3" data-testid="coverage-gaps-list">
          <p className="text-xs font-semibold mb-2">Coverage gaps{service ? ` — ${service}` : ""} <span className="text-muted-foreground font-normal">({gaps.length})</span></p>
          <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
            {gaps.map((g) => (
              <Badge key={g} variant="outline" className="text-[9px] text-red-600 dark:text-red-400 border-red-200 dark:border-red-900">{g}</Badge>
            ))}
            {gaps.length === 0 && <p className="text-xs text-emerald-600 dark:text-emerald-400">Full UK coverage — no gaps 🎉</p>}
          </div>
        </div>
      </div>
    </div>
  );
};
